// Shared types, constants and helpers for the EventConnect conference platform.
import { supabase } from '../services/supabase';

export const C = {
  navy: '#0A1628', navyHover: '#111E35', gold: '#C9A84C', black: '#0A0A0A',
  white: '#FFFFFF', surface: '#F5F6F8', border: '#D0D5DD', borderLt: '#E8EBF0',
  text: '#0A0A0A', textSec: '#3D4552', muted: '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger: '#C0222A', amber: '#92640A', teal: '#0D6E8A',
};

// ── Types ──────────────────────────────────────────────────────────────────
export type EventStatus =
  | 'draft' | 'open' | 'manager_setup' | 'investor_preferences'
  | 'scheduling' | 'published' | 'live' | 'completed';

export type SessionType =
  | 'keynote' | 'panel' | 'opening_remarks' | 'closing_remarks' | 'breakout' | 'networking';

export type RoleAtEvent = 'presenting_manager' | 'npm_manager' | 'allocator';
export type RegistrationStatus = 'interested' | 'selected' | 'confirmed' | 'declined';
export type SlotStatus = 'ai_suggested' | 'confirmed' | 'rejected';
export type AssignmentType = 'ai_assigned' | 'pb_override' | 'investor_swap' | 'ai_suggested_alt';
export type NPMMeetingStatus = 'pending' | 'accepted' | 'declined';
export type TargetAudience = 'all' | 'managers' | 'allocators' | 'presenting_managers' | 'npm_managers';

export interface EventRow {
  id: string;
  created_by: string | null;
  name: string;
  description: string | null;
  location: string | null;
  venue: string | null;
  start_date: string;
  end_date: string;
  status: EventStatus;
  max_attendees_per_firm: number;
  concurrent_presenting_slots: number;
  investor_preference_limit: number;
  investor_preference_buffer: number;
  session_duration_minutes: number;
  plenary_duration_minutes: number;
  npm_meeting_duration_minutes: number;
  schedule_change_deadline_days: number;
  agenda_url: string | null;
  pulse_survey_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlenarySession {
  id: string;
  event_id: string;
  title: string;
  session_type: SessionType;
  is_plenary: boolean;
  start_time: string;
  end_time: string;
  location: string | null;
  description: string | null;
  order_index: number;
  created_at?: string;
}

export interface Speaker {
  id: string;
  event_id: string;
  plenary_session_id: string | null;
  name: string;
  title: string | null;
  company: string | null;
  bio: string | null;
  photo_url: string | null;
  order_index: number;
  created_at?: string;
}

export interface Registration {
  id: string;
  event_id: string;
  profile_id: string;
  firm_id: string | null;
  role_at_event: RoleAtEvent | null;
  status: RegistrationStatus;
  registered_at: string;
  // joined
  profile?: { id: string; full_name: string; email: string; role: string; avatar_url: string | null };
  firm?: { id: string; name: string; type: string };
}

export interface Attendee {
  id: string;
  event_id: string;
  firm_id: string | null;
  profile_id: string | null;
  name: string;
  email: string | null;
  title: string | null;
  is_external: boolean;
  created_at?: string;
}

export interface Round {
  id: string;
  event_id: string;
  round_number: number;
  start_time: string;
  end_time: string;
  created_at?: string;
}

export interface ManagerSlot {
  id: string;
  event_id: string;
  round_id: string | null;
  firm_id: string | null;
  room: string | null;
  capacity: number;
  status: SlotStatus;
  ai_score: number | null;
  created_at?: string;
  // joined
  round?: Round;
  firm?: { id: string; name: string; type: string };
}

export interface ManagerAvailability {
  id: string;
  event_id: string;
  firm_id: string | null;
  available_round_numbers: number[];
  can_present_multiple_rounds: boolean;
  submitted_at: string;
}

export interface ManagerExclusion {
  id: string;
  event_id: string;
  manager_firm_id: string | null;
  excluded_profile_id: string | null;
  excluded_by: string | null;
  reason: string | null;
  created_at?: string;
}

export interface PBExclusion {
  id: string;
  event_id: string;
  manager_slot_id: string | null;
  excluded_profile_id: string | null;
  excluded_by: string | null;
  reason: string | null;
  created_at?: string;
}

export interface InvestorPreference {
  id: string;
  event_id: string;
  profile_id: string | null;
  firm_id: string | null;
  manager_firm_id: string | null;
  preference_rank: number;
  submitted_at: string;
  // joined
  profile?: { id: string; full_name: string };
  manager_firm?: { id: string; name: string };
}

export interface InvestorSchedule {
  id: string;
  event_id: string;
  profile_id: string | null;
  firm_id: string | null;
  manager_slot_id: string | null;
  assignment_type: AssignmentType;
  preference_rank: number | null;
  status: 'confirmed' | 'swapped_out';
  created_at?: string;
}

export interface NPMAvailability {
  id: string;
  event_id: string;
  npm_firm_id: string | null;
  profile_id: string | null;
  start_time: string;
  end_time: string;
  is_booked: boolean;
  created_at?: string;
}

export interface NPMMeeting {
  id: string;
  event_id: string;
  npm_availability_id: string | null;
  npm_firm_id: string | null;
  npm_profile_id: string | null;
  investor_profile_id: string | null;
  investor_firm_id: string | null;
  status: NPMMeetingStatus;
  requested_at: string;
  responded_at: string | null;
}

export interface EventNotification {
  id: string;
  event_id: string;
  created_by: string | null;
  title: string;
  message: string;
  target_audience: TargetAudience;
  sent_at: string;
}

export interface EventAnalytics {
  id: string;
  event_id: string;
  total_registrations: number;
  total_presenting_managers: number;
  total_npm_managers: number;
  total_allocators: number;
  total_presenting_slots: number;
  total_slots_filled: number;
  total_npm_meetings_requested: number;
  total_npm_meetings_confirmed: number;
  avg_preferences_per_investor: number | null;
  avg_preference_satisfaction_rate: number | null;
  updated_at: string;
}

// ── Constants ────────────────────────────────────────────────────────────────
export const STATUS_META: Record<EventStatus, { label: string; bg: string; fg: string; bd: string }> = {
  draft:                { label: 'Draft',                  bg: C.surface,  fg: C.muted,   bd: C.border },
  open:                 { label: 'Open for Registration',  bg: '#EFF6FF',  fg: C.teal,    bd: '#BFDBFE' },
  manager_setup:        { label: 'Manager Setup',          bg: '#FEFCE8',  fg: C.amber,   bd: '#FDE68A' },
  investor_preferences: { label: 'Preferences Open',       bg: '#FEFCE8',  fg: C.amber,   bd: '#FDE68A' },
  scheduling:           { label: 'Scheduling',             bg: '#FEFCE8',  fg: C.amber,   bd: '#FDE68A' },
  published:            { label: 'Schedule Published',     bg: '#F0FDF4',  fg: C.success, bd: '#BBF7D0' },
  live:                 { label: 'Live',                   bg: '#F0FDF4',  fg: C.success, bd: '#BBF7D0' },
  completed:            { label: 'Completed',              bg: C.surface,  fg: C.muted,   bd: C.border },
};

export const SESSION_TYPES: { value: SessionType; label: string }[] = [
  { value: 'keynote', label: 'Keynote' },
  { value: 'panel', label: 'Panel' },
  { value: 'opening_remarks', label: 'Opening Remarks' },
  { value: 'closing_remarks', label: 'Closing Remarks' },
  { value: 'breakout', label: 'Breakout' },
  { value: 'networking', label: 'Networking' },
];

export const TARGET_AUDIENCES: { value: TargetAudience; label: string }[] = [
  { value: 'all', label: 'All Attendees' },
  { value: 'managers', label: 'Managers Only' },
  { value: 'allocators', label: 'Allocators Only' },
  { value: 'presenting_managers', label: 'Presenting Managers' },
  { value: 'npm_managers', label: 'NPM Managers' },
];

export const ROLE_LABELS: Record<RoleAtEvent, string> = {
  presenting_manager: 'Presenting Manager',
  npm_manager: 'NPM Manager',
  allocator: 'Allocator',
};

// ── Date / time helpers ──────────────────────────────────────────────────────
export function fmtDate(ts: string | null): string {
  return ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
}

export function fmtDateShort(ts: string | null): string {
  return ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '-';
}

export function fmtDateTime(ts: string | null): string {
  return ts ? new Date(ts).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
}

export function fmtTime(ts: string | null): string {
  return ts ? new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-';
}

export function fmtTimeRange(start: string, end: string): string {
  return `${fmtTime(start)} – ${fmtTime(end)}`;
}

export function fmtDateRange(start: string, end: string): string {
  const s = new Date(start), e = new Date(end);
  if (s.toDateString() === e.toDateString()) return fmtDate(start);
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `${s.getDate()}–${e.getDate()} ${e.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`;
  }
  return `${fmtDate(start)} – ${fmtDate(end)}`;
}

export function fmtDayLabel(ts: string): string {
  return new Date(ts).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

export function durationMinutes(start: string, end: string): number {
  return Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
}

export function fmtDuration(start: string, end: string): string {
  const m = durationMinutes(start, end);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h}h ${r}m` : `${h} hr`;
}

// Format a date for a <input type="datetime-local"> value (local time, no tz).
export function toLocalInput(ts: string | null): string {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// AUM range label from firm min/max/exact (values stored in millions or absolute?).
export function aumLabel(firm?: { aum_min?: number; aum_max?: number; aum_exact?: number } | null): string {
  if (!firm) return '';
  const fmt = (v: number) => {
    if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(1)}B`;
    if (v >= 1_000_000) return `$${Math.round(v / 1_000_000)}M`;
    if (v >= 1000) return `$${Math.round(v / 1000)}M`; // stored in thousands
    return `$${v}M`;
  };
  if (firm.aum_exact) return fmt(firm.aum_exact);
  if (firm.aum_min && firm.aum_max) return `${fmt(firm.aum_min)}–${fmt(firm.aum_max)}`;
  if (firm.aum_min) return `${fmt(firm.aum_min)}+`;
  return '';
}

export function firstName(fullName: string | null | undefined): string {
  if (!fullName) return 'Attendee';
  return fullName.trim().split(/\s+/)[0];
}

// ── Notifications (platform in-app) ──────────────────────────────────────────
interface NotifRow { user_id: string; type: string; title: string; body: string; link: string; }

export async function notifyUsers(rows: NotifRow[]): Promise<void> {
  if (!rows.length) return;
  await supabase.from('notifications').insert(rows);
}

export async function notifyManagersSelected(ev: EventRow, profileIds: string[]): Promise<void> {
  await notifyUsers(profileIds.map(id => ({
    user_id: id,
    type: 'event_selected',
    title: 'You have been selected for a conference',
    body: `${ev.name}, please complete your event setup.`,
    link: `/event-connect/setup/${ev.id}`,
  })));
}

export async function notifyPreferencesOpen(ev: EventRow, profileIds: string[]): Promise<void> {
  await notifyUsers(profileIds.map(id => ({
    user_id: id,
    type: 'event_preferences',
    title: 'Meeting preferences are open',
    body: `${ev.name}, submit your presenting-manager preferences.`,
    link: `/event-connect/preferences/${ev.id}`,
  })));
}

export async function notifySchedulePublished(ev: EventRow, profileIds: string[]): Promise<void> {
  await notifyUsers(profileIds.map(id => ({
    user_id: id,
    type: 'event_schedule',
    title: 'Your conference schedule is ready',
    body: `${ev.name}, view your personal schedule.`,
    link: `/event-connect`,
  })));
}

// Broadcast a PB notification: writes the event_notifications record AND fans out
// per-user rows to the platform notifications table for the target audience.
export async function sendEventNotification(
  ev: EventRow,
  createdBy: string | null,
  title: string,
  message: string,
  targetAudience: TargetAudience,
  recipientProfileIds: string[],
): Promise<void> {
  await supabase.from('event_notifications').insert({
    event_id: ev.id, created_by: createdBy, title, message, target_audience: targetAudience,
  });
  await notifyUsers(recipientProfileIds.map(id => ({
    user_id: id, type: 'event_broadcast', title, body: message, link: '/event-connect',
  })));
}

// ── JSON parsing (loose) ─────────────────────────────────────────────────────
export function parseJsonLoose<T>(text: string): T | null {
  if (!text) return null;
  try { return JSON.parse(text) as T; } catch { /* fall through */ }
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fence) { try { return JSON.parse(fence[1]) as T; } catch { /* */ } }
  const first = text.indexOf('{'); const firstArr = text.indexOf('[');
  const start = firstArr !== -1 && (first === -1 || firstArr < first) ? firstArr : first;
  const lastObj = text.lastIndexOf('}'); const lastArr = text.lastIndexOf(']');
  const end = Math.max(lastObj, lastArr);
  if (start !== -1 && end > start) {
    try { return JSON.parse(text.slice(start, end + 1)) as T; } catch { /* */ }
  }
  return null;
}

// ════════════════════════════════════════════════════════════════════════════
//  AI SCHEDULING  (deterministic core + altveno-ai narrative)
// ════════════════════════════════════════════════════════════════════════════

export interface SlotAssignment {
  firm_id: string;
  firm_name: string;
  round_number: number;
  ai_score: number;
  reason?: string;
}

export interface SlotBuilderInput {
  managers: { firm_id: string; firm_name: string }[];
  availability: Record<string, { rounds: number[]; multiple: boolean }>; // keyed by firm_id
  roundCount: number;
  concurrentSlots: number;
}

export interface SlotBuilderResult {
  assignments: SlotAssignment[];
  roundsNeeded: number;
  narrative: string;
  unassigned: string[]; // firm names that could not be placed
}

// Deterministic assignment: place each manager into an available round while
// keeping each round at/under the concurrent-slot cap. Greedy, availability-aware.
export function buildSlotsDeterministic(input: SlotBuilderInput): SlotBuilderResult {
  const { managers, availability, roundCount, concurrentSlots } = input;
  const roundsNeeded = Math.max(roundCount, Math.ceil(managers.length / Math.max(1, concurrentSlots)));
  const load: Record<number, number> = {};
  for (let r = 1; r <= roundsNeeded; r++) load[r] = 0;

  // Order managers by fewest available rounds first (most constrained wins).
  const ordered = [...managers].sort((a, b) => {
    const av = availability[a.firm_id]?.rounds.length ?? roundsNeeded;
    const bv = availability[b.firm_id]?.rounds.length ?? roundsNeeded;
    return av - bv;
  });

  const assignments: SlotAssignment[] = [];
  const unassigned: string[] = [];

  for (const m of ordered) {
    const avail = availability[m.firm_id]?.rounds?.length
      ? availability[m.firm_id].rounds.filter(r => r >= 1 && r <= roundsNeeded)
      : Array.from({ length: roundsNeeded }, (_, i) => i + 1);
    // pick the available round with the least current load
    const candidates = avail
      .filter(r => load[r] < concurrentSlots)
      .sort((a, b) => load[a] - load[b]);
    const chosen = candidates.length ? candidates[0] : avail.sort((a, b) => load[a] - load[b])[0];
    if (chosen === undefined) { unassigned.push(m.firm_name); continue; }
    load[chosen] += 1;
    const flexibility = availability[m.firm_id]?.rounds.length ?? roundsNeeded;
    const overCap = load[chosen] > concurrentSlots;
    const score = Math.max(0.4, Math.min(1, (flexibility / roundsNeeded) * 0.5 + (overCap ? 0.3 : 0.6)));
    assignments.push({
      firm_id: m.firm_id, firm_name: m.firm_name, round_number: chosen,
      ai_score: Math.round(score * 100) / 100,
      reason: overCap ? 'Placed above target capacity, review' : 'Availability-optimal placement',
    });
  }
  const narrative =
    `${managers.length} presenting managers arranged across ${roundsNeeded} rounds ` +
    `(target ${concurrentSlots} concurrent slots per round). ` +
    `${unassigned.length ? `${unassigned.length} could not be placed within availability. ` : 'All managers placed within their stated availability. '}` +
    `Most-constrained managers were seated first to maximise overall coverage.`;
  return { assignments, roundsNeeded, narrative, unassigned };
}

// Ask altveno-ai to review / refine the deterministic plan. Falls back to the
// deterministic result if the model output cannot be parsed.
export async function runAISlotBuilder(input: SlotBuilderInput): Promise<SlotBuilderResult> {
  const base = buildSlotsDeterministic(input);
  try {
    const systemPrompt =
      `You are a conference operations scheduler for an alternative investment platform. ` +
      `Assign presenting managers to time rounds. Rules: each manager must be in a round they are available for; ` +
      `keep at most ${input.concurrentSlots} managers per round where possible; use exactly ${base.roundsNeeded} rounds. ` +
      `Return ONLY JSON: {"assignments":[{"firm_id":"...","round_number":N,"ai_score":0-1,"reason":"..."}],"narrative":"..."}`;
    const managerLines = input.managers.map(m => {
      const a = input.availability[m.firm_id];
      return `- ${m.firm_name} (id ${m.firm_id}) available rounds: ${a?.rounds.length ? a.rounds.join(',') : 'any'}${a?.multiple ? ' (can present multiple)' : ''}`;
    }).join('\n');
    const { data, error } = await supabase.functions.invoke('altveno-ai', {
      body: { systemPrompt, messages: [{ role: 'user', content: `Managers:\n${managerLines}\n\nProduce the assignment JSON.` }] },
    });
    if (error || data?.error) return base;
    const parsed = parseJsonLoose<{ assignments: { firm_id: string; round_number: number; ai_score?: number; reason?: string }[]; narrative?: string }>(data?.content?.[0]?.text || '');
    if (!parsed?.assignments?.length) return base;
    const nameFor = new Map(input.managers.map(m => [m.firm_id, m.firm_name]));
    const assignments: SlotAssignment[] = parsed.assignments
      .filter(a => nameFor.has(a.firm_id) && a.round_number >= 1 && a.round_number <= base.roundsNeeded)
      .map(a => ({
        firm_id: a.firm_id, firm_name: nameFor.get(a.firm_id)!,
        round_number: a.round_number,
        ai_score: typeof a.ai_score === 'number' ? Math.round(a.ai_score * 100) / 100 : 0.8,
        reason: a.reason,
      }));
    if (assignments.length < input.managers.length * 0.8) return base; // AI dropped too many → trust deterministic
    const placed = new Set(assignments.map(a => a.firm_id));
    const unassigned = input.managers.filter(m => !placed.has(m.firm_id)).map(m => m.firm_name);
    return { assignments, roundsNeeded: base.roundsNeeded, narrative: parsed.narrative || base.narrative, unassigned };
  } catch {
    return base;
  }
}

// ── Investor schedule optimisation ───────────────────────────────────────────
export interface OptimiseInput {
  // investors keyed by profile_id
  investors: { profile_id: string; firm_id: string | null; name: string }[];
  // ranked manager_firm_id preferences per investor (rank order)
  preferences: Record<string, { manager_firm_id: string; rank: number }[]>;
  // slots available: manager_firm_id -> {slot_id, round_number, capacity}
  slots: { slot_id: string; firm_id: string; round_number: number; capacity: number }[];
  // exclusions: set of `${manager_firm_id}:${profile_id}` (manager excluded investor)
  managerExclusions: Set<string>;
  // pb exclusions: set of `${slot_id}:${profile_id}`
  pbExclusions: Set<string>;
}

export interface ScheduleAssignment {
  profile_id: string;
  slot_id: string;
  firm_id: string;
  round_number: number;
  preference_rank: number | null;
  assignment_type: AssignmentType;
}

export interface OptimiseResult {
  assignments: ScheduleAssignment[];
  satisfactionRate: number; // % of assignments that matched a stated preference
  rankBreakdown: Record<number, number>; // rank -> count (0 = suggested alt)
  narrative: string;
}

// Greedy preference matcher honouring capacity, per-round conflicts and exclusions.
export function optimiseScheduleDeterministic(input: OptimiseInput): OptimiseResult {
  const { investors, preferences, slots, managerExclusions, pbExclusions } = input;
  const slotFill: Record<string, number> = {};
  slots.forEach(s => { slotFill[s.slot_id] = 0; });
  const slotByFirm: Record<string, typeof slots[number][]> = {};
  slots.forEach(s => { (slotByFirm[s.firm_id] ||= []).push(s); });

  const assignments: ScheduleAssignment[] = [];
  const rankBreakdown: Record<number, number> = {};
  let matched = 0;

  // Sort investors by number of preferences ascending so the constrained get first pick.
  const ordered = [...investors].sort((a, b) => (preferences[a.profile_id]?.length ?? 0) - (preferences[b.profile_id]?.length ?? 0));

  for (const inv of ordered) {
    const prefs = (preferences[inv.profile_id] || []).slice().sort((a, b) => a.rank - b.rank);
    const usedRounds = new Set<number>();
    for (const pref of prefs) {
      if (managerExclusions.has(`${pref.manager_firm_id}:${inv.profile_id}`)) continue;
      const candidateSlots = (slotByFirm[pref.manager_firm_id] || [])
        .filter(s => !usedRounds.has(s.round_number))
        .filter(s => !pbExclusions.has(`${s.slot_id}:${inv.profile_id}`))
        .filter(s => slotFill[s.slot_id] < s.capacity)
        .sort((a, b) => slotFill[a.slot_id] - slotFill[b.slot_id]);
      const slot = candidateSlots[0];
      if (slot) {
        slotFill[slot.slot_id] += 1;
        usedRounds.add(slot.round_number);
        assignments.push({
          profile_id: inv.profile_id, slot_id: slot.slot_id, firm_id: slot.firm_id,
          round_number: slot.round_number, preference_rank: pref.rank, assignment_type: 'ai_assigned',
        });
        rankBreakdown[pref.rank] = (rankBreakdown[pref.rank] || 0) + 1;
        matched += 1;
      }
    }
  }

  // Fill remaining capacity for investors who got fewer than 2 sessions with
  // non-excluded alternatives (suggested alternates).
  for (const inv of ordered) {
    const mine = assignments.filter(a => a.profile_id === inv.profile_id);
    if (mine.length >= 2) continue;
    const usedRounds = new Set(mine.map(a => a.round_number));
    const alt = slots
      .filter(s => !usedRounds.has(s.round_number))
      .filter(s => !pbExclusions.has(`${s.slot_id}:${inv.profile_id}`))
      .filter(s => !managerExclusions.has(`${s.firm_id}:${inv.profile_id}`))
      .filter(s => slotFill[s.slot_id] < s.capacity)
      .sort((a, b) => slotFill[a.slot_id] - slotFill[b.slot_id])[0];
    if (alt) {
      slotFill[alt.slot_id] += 1;
      assignments.push({
        profile_id: inv.profile_id, slot_id: alt.slot_id, firm_id: alt.firm_id,
        round_number: alt.round_number, preference_rank: null, assignment_type: 'ai_suggested_alt',
      });
      rankBreakdown[0] = (rankBreakdown[0] || 0) + 1;
    }
  }

  const satisfactionRate = assignments.length ? Math.round((matched / assignments.length) * 1000) / 10 : 0;
  const narrative =
    `${assignments.length} assignments generated for ${investors.length} investors. ` +
    `${satisfactionRate}% of assignments matched a stated preference; ` +
    `${rankBreakdown[0] || 0} were suggested alternatives to fill open capacity. ` +
    `Constrained investors (fewest preferences) were matched first; per-round conflicts and all exclusions were respected.`;
  return { assignments, satisfactionRate, rankBreakdown, narrative };
}

export async function runAIScheduleOptimisation(input: OptimiseInput): Promise<OptimiseResult> {
  // The matching is combinatorial and correctness-critical, so we run the
  // deterministic optimiser and ask altveno-ai only for a written summary.
  const result = optimiseScheduleDeterministic(input);
  try {
    const systemPrompt =
      `You are an events analyst. In 2-3 sentences, summarise the quality of an investor-meeting schedule for an industry conference. ` +
      `Be concise, neutral and specific. Do not invent numbers.`;
    const { data, error } = await supabase.functions.invoke('altveno-ai', {
      body: {
        systemPrompt,
        messages: [{ role: 'user', content:
          `Investors: ${input.investors.length}. Assignments: ${result.assignments.length}. ` +
          `Preference-match rate: ${result.satisfactionRate}%. ` +
          `Rank breakdown (rank:count): ${Object.entries(result.rankBreakdown).map(([r, c]) => `${r === '0' ? 'alt' : r}:${c}`).join(', ')}. ` +
          `Write the summary.` }],
      },
    });
    if (!error && !data?.error) {
      const txt = data?.content?.[0]?.text;
      if (txt) return { ...result, narrative: txt };
    }
  } catch { /* keep deterministic narrative */ }
  return result;
}

// ── Room suggestion ──────────────────────────────────────────────────────────
export interface RoomSuggestInput {
  slots: { slot_id: string; firm_id: string; firm_name: string; round_number: number; investorCount: number }[];
  rooms: string[]; // available room names
}
// Assign rooms keeping each firm in the same room across rounds where possible,
// and matching larger rooms (later in the list) to higher investor counts.
export function suggestRooms(input: RoomSuggestInput): Record<string, string> {
  const { slots, rooms } = input;
  if (!rooms.length) return {};
  const firmRoom: Record<string, string> = {};
  const perRoundUsed: Record<number, Set<string>> = {};
  const result: Record<string, string> = {};
  // Order by investor count desc so busy sessions get first pick of rooms.
  const ordered = [...slots].sort((a, b) => b.investorCount - a.investorCount);
  for (const s of ordered) {
    (perRoundUsed[s.round_number] ||= new Set());
    let room = firmRoom[s.firm_id];
    if (!room || perRoundUsed[s.round_number].has(room)) {
      room = rooms.find(r => !perRoundUsed[s.round_number].has(r)) || rooms[0];
    }
    firmRoom[s.firm_id] = room;
    perRoundUsed[s.round_number].add(room);
    result[s.slot_id] = room;
  }
  return result;
}

// ── CSV export ───────────────────────────────────────────────────────────────
export function toCSV(rows: (string | number | null)[][]): string {
  return rows.map(r => r.map(cell => {
    const s = cell === null || cell === undefined ? '' : String(cell);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(',')).join('\r\n');
}

export function downloadCSV(filename: string, csv: string): void {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── ICS (calendar) export ────────────────────────────────────────────────────
export interface ICSEvent {
  uid: string;
  title: string;
  start: string; // ISO
  end: string;   // ISO
  location?: string;
  description?: string;
}

function icsDate(ts: string): string {
  // UTC basic format YYYYMMDDTHHMMSSZ
  return new Date(ts).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function icsEscape(s: string): string {
  return (s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

export function generateICS(calName: string, events: ICSEvent[]): string {
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Altveno//EventConnect//EN',
    'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', `X-WR-CALNAME:${icsEscape(calName)}`,
  ];
  const stamp = icsDate(new Date().toISOString());
  for (const e of events) {
    lines.push(
      'BEGIN:VEVENT',
      `UID:${e.uid}@altveno`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${icsDate(e.start)}`,
      `DTEND:${icsDate(e.end)}`,
      `SUMMARY:${icsEscape(e.title)}`,
    );
    if (e.location) lines.push(`LOCATION:${icsEscape(e.location)}`);
    if (e.description) lines.push(`DESCRIPTION:${icsEscape(e.description)}`);
    lines.push('END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

export function downloadICS(filename: string, calName: string, events: ICSEvent[]): void {
  const blob = new Blob([generateICS(calName, events)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
